import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import { API_URLS } from './config';
import type { ApiResponse } from '../types/ApiResponse';
import type { AudioProcessingOptions } from '../types/AudioProcessingOptions';
import type { TextProcessingOptions } from '../types/TextProcessingOptions';
import type { AudioProcessingResponse } from '../types/AudioProcessingResponse';
import { useTutor } from '../contexts/TutorContext';
import axios from 'axios';

export * from './types';

export type SupportedLanguage = 'en' | 'es' | 'pt' | 'fr' | 'de' | 'it' | 'ru';

// Helper function to get short language code
const getShortLanguageCode = (language: string): string => {
  if (!language) return 'en'; // Default to English if no language provided
  
  // If it's already a short code, return as is
  if (language.length === 2) {
    return language;
  }
  
  // If it's a full code (e.g., 'en-US'), take first part
  return language.split('-')[0];
};

// Helper function to create FormData for web platform
const createWebFormData = async (audioData: string | Blob, options: AudioProcessingOptions): Promise<FormData> => {
  const formData = new FormData();
  
  try {
    // If we have a blob URL, fetch the actual blob
    let blob: Blob;
    if (typeof audioData === 'string' && audioData.startsWith('blob:')) {
      const response = await fetch(audioData);
      blob = await response.blob();
    } else if (audioData instanceof Blob) {
      blob = audioData;
    } else {
      throw new Error('Invalid audio data format for web platform');
    }
    
    // For web: append blob with a filename
    formData.append('audio', blob, 'recording.webm');
    formData.append('language', getShortLanguageCode(options.language));
    formData.append('platform', 'web');
    
    if (options.session_id) {
      formData.append('session_id', options.session_id);
    }
    
    if (options.user_info) {
      formData.append('user_info', JSON.stringify(options.user_info));
    }
    
    return formData;
  } catch (error) {
    throw error;
  }
};



// Process audio recording
export const processAudio = async (
  audioData: Blob | string,
  params: {
    language: string;
    tutorId: string;
    sessionId: string;
    platform: string;
  }
): Promise<ApiResponse> => {
  try {
    const formData = new FormData();
    
    if (typeof audioData === 'string') {
      // For React Native, we need to create a file from the URI
      const response = await fetch(audioData);
      const blob = await response.blob();
      formData.append('audio', blob, 'recording.m4a');
    } else {
      // For web, ensure we're sending with the correct filename and type
      const fileExtension = audioData.type.includes('webm') ? 'webm' : 'm4a';
      formData.append('audio', audioData, `recording.${fileExtension}`);
    }

    // Add other parameters
    formData.append('language', params.language);
    formData.append('tutor_id', params.tutorId);
    formData.append('session_id', params.sessionId);
    

    console.log('Sending audio file:', {
      type: typeof audioData === 'string' ? 'm4a' : audioData.type,
      size: typeof audioData === 'string' ? 'unknown' : audioData.size,
      language: params.language,
      tutorId: params.tutorId
    });

    const response = await axios.post<ApiResponse>(
      API_URLS.audioMessage,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json',
        },
        withCredentials: false,
      }
    );

    // Log the response for debugging
    console.log('Audio processing response:', {
      status: response.data.status,
      hasTranscript: !!response.data.transcript,
      hasResponse: !!response.data.response
    });

    return response.data;
  } catch (error) {
    console.error('Error processing audio:', error);
    throw error;
  }
};

// Process text message
export async function processText({
  text,
  language,
  tutorId,
  sessionId
}: {
  text: string;
  language: string;
  tutorId: string;
  sessionId: string;
}): Promise<ApiResponse> {
  try {
    const shortLanguage = getShortLanguageCode(language);
    const url = API_URLS.chatText;
    
    console.log('Processing text with:', { text, language: shortLanguage, tutorId, sessionId, url });
    
    const response = await fetch(API_URLS.chatText, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        language: shortLanguage,
        tutor_id: tutorId,
        session_id: sessionId,
        system_prompt: "You are a helpful tutor who assists students with their questions."
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in processText:', error);
    throw error;
  }
};
